import { parseCalendarDate } from '@klothiq/domain';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { operationsRpc } from '@/lib/operations';

import type {
  CatalogField,
  CatalogValues,
  CatalogProfile,
  CatalogDefinition,
} from '@klothiq/domain';
export type {
  CatalogField,
  CatalogValues,
  CatalogValue,
  CatalogProfile,
  CatalogDefinition,
} from '@klothiq/domain';
export function useCatalogChoices() {
  const { organization, profile, access } = useAuth();
  return useQuery({
    queryKey: ['catalog_choices', organization?.id, profile?.id, access?.access_version],
    enabled: !!organization,
    queryFn: async () => {
      const [types, categories, audiences, ages, sizes] = await Promise.all([
        supabase
          .from('product_types')
          .select('*')
          .eq('organization_id', organization!.id)
          .order('display_order'),
        supabase
          .from('categories')
          .select('*')
          .eq('organization_id', organization!.id)
          .order('name'),
        supabase
          .from('target_audiences')
          .select('*')
          .eq('organization_id', organization!.id)
          .order('display_order'),
        supabase
          .from('age_groups')
          .select('*')
          .eq('organization_id', organization!.id)
          .order('display_order'),
        supabase
          .from('size_sets')
          .select('*')
          .eq('organization_id', organization!.id)
          .order('name')
          .order('id'),
      ]);
      if (types.error) throw types.error;
      if (categories.error) throw categories.error;
      if (audiences.error) throw audiences.error;
      if (ages.error) throw ages.error;
      if (sizes.error) throw sizes.error;
      return {
        types: types.data || [],
        categories: categories.data || [],
        audiences: audiences.data || [],
        ages: ages.data || [],
        sizes: sizes.data || [],
      };
    },
  });
}
export function useCatalogProfile(id?: string, version?: string | null) {
  const { organization, profile, access } = useAuth();
  return useQuery({
    queryKey: [
      'catalog_profile',
      organization?.id,
      profile?.id,
      access?.access_version,
      id,
      version,
    ],
    enabled: !!id && !!organization,
    queryFn: () =>
      operationsRpc<CatalogProfile>('get_catalog_profile_v1', {
        p_profile_id: id,
        p_schema_version_id: version || null,
        p_legacy: version === null,
      }),
  });
}
export function catalogIssues(
  fields: CatalogField[],
  values: CatalogValues,
  old: CatalogValues = {},
  level: 'product' | 'variant' = 'product',
) {
  const errors: Record<string, string> = {};
  for (const f of fields.filter((f) => f.level === level)) {
    if (f.when && values[f.when.field] !== f.when.equals) continue;
    const v = values[f.code];
    if (
      Object.prototype.hasOwnProperty.call(old, f.code) &&
      JSON.stringify(old[f.code]) === JSON.stringify(v) &&
      (!f.when || old[f.when.field] === f.when.equals)
    )
      continue;
    if (
      v === undefined ||
      v === null ||
      (typeof v === 'string' && v.trim() === '') ||
      (Array.isArray(v) && !v.length)
    ) {
      if (f.required) errors[f.code] = `${f.label} is required`;
      continue;
    }
    if (
      f.type === 'number' &&
      (typeof v !== 'number' ||
        !Number.isFinite(v) ||
        (f.min !== undefined && v < f.min) ||
        (f.max !== undefined && v > f.max))
    )
      errors[f.code] = `Check ${f.label}`;
    if (f.type === 'text' && (typeof v !== 'string' || v.length > (f.maxLength || 1000)))
      errors[f.code] = `Check ${f.label}`;
    if (f.type === 'date' && (typeof v !== 'string' || !parseCalendarDate(v)))
      errors[f.code] = `Check ${f.label}`;
    if (f.type === 'boolean' && typeof v !== 'boolean') errors[f.code] = `Choose ${f.label}`;
    if (
      (f.type === 'select' || f.type === 'multiselect') &&
      !(Array.isArray(v) ? v : [v]).every((k) =>
        f.options?.some((o) => o.value === k && o.active !== false),
      )
    )
      errors[f.code] = `Select an active ${f.label}`;
  }
  return errors;
}

export interface CatalogVersion {
  id: string;
  version: number;
  status: 'draft' | 'published' | 'deactivated';
  definition: CatalogDefinition;
}
export function useCatalogVersions(id: string) {
  const { organization, profile, access } = useAuth();
  return useQuery({
    queryKey: ['catalog_versions', organization?.id, profile?.id, access?.access_version, id],
    enabled: !!organization && !!id,
    queryFn: async () => {
      const all: CatalogVersion[] = [];
      for (let offset = 0; ; offset += 500) {
        const rows = await operationsRpc<CatalogVersion[]>('list_catalog_versions_v1', {
          p_profile_id: id,
          p_offset: offset,
        });
        all.push(...rows);
        if (rows.length < 500) return all;
      }
    },
  });
}
